import { useMemo } from 'react';
import { createInfiniteQuery } from 'react-query-kit';
import { keepPreviousData } from '@tanstack/react-query';
import type { QuizsetSearchReq } from '@/api/gen/_model/quizsetSearchReq';
import { QuizsetSearchReqOrder } from '@/api/gen/_model/quizsetSearchReqOrder';
import type { SearchResponseListSearchQuizsetRes } from '@/api/gen/_model/searchResponseListSearchQuizsetRes';
import { searchUserPublicQuizsets } from '@/api/gen/quiz-user/quiz-user.gen';
import { useSearchQuizQuery } from '@/module/public/hooks/useSearchQuizQuery';
export const isQuizsetSearchReqOrder = (value: unknown): value is QuizsetSearchReqOrder => {
  return typeof value === 'string' && value in QuizsetSearchReqOrder;
};
export const getDefaultQuizsetSearchReq = ({
  searchText,
  order
}: {
  searchText: string;
  order: string;
}): QuizsetSearchReq => {
  return {
    categoryFilters: {
      criteria: {
        ...(searchText?.length > 0 && {
          searchText: searchText,
          searchType: 'TITLE'
        })
      }
    },
    order: isQuizsetSearchReqOrder(order) ? order : QuizsetSearchReqOrder.RECENTLY,
    limit: 20,
    page: 1
  };
};
export const useUserQuizes = (userId: string) => {
  const {
    searchText,
    order
  } = useSearchQuizQuery();
  const result = useFetchUserQuizes({
    enabled: true,
    variables: {
      userId,
      quizsetSearchReq: getDefaultQuizsetSearchReq({
        searchText,
        order
      })
    }
  });
  const flatData = useMemo(() => result.data?.pages.flatMap(page => page.data ?? []) ?? [], [result.data]);
  return {
    ...result,
    flatData,
    totalCount: result?.data?.pages[0].totalCount ?? 0,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    isFetchingNextPage: result.isFetchingNextPage
  };
};
export const useFetchUserQuizes = createInfiniteQuery<SearchResponseListSearchQuizsetRes, {
  userId: string;
  quizsetSearchReq: QuizsetSearchReq;
}>({
  queryKey: ['quizsets/search'],
  fetcher: async (variables, pageParam) => {
    return searchUserPublicQuizsets(variables.userId, {
      ...variables.quizsetSearchReq,
      page: pageParam.pageParam
    });
  },
  getNextPageParam: lastPage => {
    if (!lastPage?.currentPage || !lastPage?.totalPages) {
      return undefined;
    }
    return lastPage?.currentPage < lastPage?.totalPages ? lastPage.currentPage + 1 : undefined;
  },
  initialPageParam: 1,
  placeholderData: keepPreviousData
});